import every from "lodash/every";
import keys from "lodash/keys";
import { CvExportsButton } from "PFApp/components/cv_exports_button";
import { Action, SubsidiaryModeAlert } from "PFApp/components/subsidiary_mode_alert";
import { LegacyLazyLocalExportTrigger } from "PFApp/legacy_cv_export_flow";
import { Flex } from "PFComponents/containers";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { useProfiles } from "PFCore/hooks/queries/profiles/use_profiles";
import { FeatureFlag, PermissionRule } from "PFCore/types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useProfilesPageContext } from "../../context";
import css from "./bulk_select_overlay.module.scss";
import { DeletePlaceholderProfilesModal } from "./delete_placeholder_profiles_modal";
import { getLegacyCvExportSelectedProfilesCollection } from "./legacy_cv_export_selected_profiles_collection";

export const BulkSelectOverlay = () => {
  const { t } = useTranslation(["translation", "profiles"]);
  const { data: currentProfile } = useCurrentProfile();
  const canDeletePlaceholders = useIsPermittedTo(currentProfile)(PermissionRule.DeletePlaceholderProfiles);
  const { selectedIds, setSelectedIds } = useProfilesPageContext();
  const selectedProfileIds = keys(selectedIds);
  const isEnabled = useIsFeatureEnabled();
  const isLegacyCvExport = isEnabled(FeatureFlag.CVExportLegacyFlow);
  const exportCvsLabel = t("profiles:list.exportCvs", { count: selectedProfileIds.length });

  const { data: { entries: selectedProfiles } = {}, isFetching } = useProfiles({
    filters: {
      fields: {
        id: selectedProfileIds
      }
    }
  });

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const isEveryProfilePlaceholder = useMemo(
    () => every(selectedProfiles, "profile.placeholder"),
    [selectedProfiles]
  );

  const actions: Action[] = [
    {
      node: isLegacyCvExport ? (
        <LegacyLazyLocalExportTrigger
          buttonKind="ghost"
          icon="export"
          label={exportCvsLabel}
          quantity={selectedProfileIds.length}
          getProfilesCollection={getLegacyCvExportSelectedProfilesCollection(selectedProfileIds)}
        />
      ) : (
        <CvExportsButton profileIds={selectedProfileIds} icon="export" kind="ghost" text={exportCvsLabel} />
      )
    },
    {
      icon: "remove",
      onClick: () => setIsDeleteModalVisible(true),
      title: t(
        isEveryProfilePlaceholder
          ? "profiles:list.placeholderProfiles.removePlaceholderProfiles"
          : "profiles:list.placeholderProfiles.removePlaceholderProfilesDisabled"
      ),
      disabled: !canDeletePlaceholders || (selectedProfiles && !isEveryProfilePlaceholder) || isFetching
    }
  ];

  return (
    <Flex center className={css.container}>
      <div className={css.overlay} />
      <SubsidiaryModeAlert
        className={css.alert}
        title={t("translation:countSelected", { count: keys(selectedIds).length })}
        onClose={() => setSelectedIds({})}
        actions={actions}
      />
      {isDeleteModalVisible && (
        <DeletePlaceholderProfilesModal onClose={() => setIsDeleteModalVisible(false)} />
      )}
    </Flex>
  );
};
